import {Injectable} from "@angular/core";
import {RemoteCommandService} from "./remote.command.service";
import {AdConversionCommandNamespace} from "../../../shared/commands/ad-conversion";
import {isInBrowser} from "../utils/clientutils";

/**
 * Идентификатор клика по рекламе: ловим на лендинге, отдаём серверу при входе в чат.
 *
 * Почему в два шага: сеть подставляет свой макрос в ссылку на лендинг (`?subid={click_id}`),
 * а конверсией считается вход в чат. К моменту, когда роутер доводит пользователя до `/chat`,
 * query-параметра в URL уже нет, поэтому значение перехватывается при старте приложения
 * и живёт в localStorage. Сам постбек шлёт сервер — см. `docs/specs/ad-postback.md`.
 */
@Injectable({
    providedIn: 'root'
})
export class AdClickService {
    private static readonly STORAGE_KEY = 'ad_click';

    /**
     * Имя параметра выбирает рекламодатель при настройке кампании, поэтому принимаем все
     * ходовые варианты: если в кабинете настроят не тот, что в коде, симптомом будет полное
     * отсутствие конверсий без единой строчки в логах.
     */
    private static readonly PARAM_NAMES = ['subid', 'click_id', 'clickid', 'kadam_click_id'];

    private static readonly NETWORK = 'kadam';

    /**
     * Идентификаторы Kadam — hex-подобная строка, но точный формат не задокументирован,
     * поэтому проверка широкая: отсекает только явный мусор и переполнение хранилища.
     */
    private static readonly CLICK_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

    /**
     * Клик, пролежавший дольше этого срока, конверсией уже не считается.
     */
    private static readonly TTL_MS = 30 * 24 * 60 * 60 * 1000;

    private reporting = false;

    constructor(private remoteCommandService: RemoteCommandService) {
    }

    /**
     * Вызывается при старте приложения — до того, как роутер уведёт с лендинга.
     */
    capture(): void {
        if (!isInBrowser()) return;

        try {
            const params = new URLSearchParams(window.location.search);

            for (const name of AdClickService.PARAM_NAMES) {
                const value = params.get(name);

                if (!value) continue;

                if (!AdClickService.CLICK_ID_PATTERN.test(value)) {
                    console.warn(`Ad click id in "${name}" has unexpected format, ignored`);
                    continue;
                }

                // Последний клик перетирает предыдущий: пользователь пришёл по свежему объявлению.
                window.localStorage.setItem(AdClickService.STORAGE_KEY, JSON.stringify({
                    clickId: value,
                    network: AdClickService.NETWORK,
                    savedAt: Date.now()
                }));

                return;
            }
        } catch (e) {
            console.warn('Cant capture ad click id', e);
        }
    }

    /**
     * Вызывается при входе в чат. Клик удаляется из localStorage только после того,
     * как сервер подтвердил, что взял его на себя.
     */
    async reportConversion(): Promise<void> {
        if (!isInBrowser() || this.reporting) return;

        const stored = this.readStored();

        if (!stored) return;

        if (Date.now() - stored.savedAt > AdClickService.TTL_MS) {
            this.clear();
            return;
        }

        this.reporting = true;

        try {
            await this.remoteCommandService.sendCommandWithCallback(
                new AdConversionCommandNamespace.Request(stored.clickId, stored.network)
            );

            this.clear();
        } catch (e) {
            // Связи нет или сервер не ответил — клик остаётся, попытка повторится при следующем входе.
            console.warn('Cant report ad conversion', e);
        } finally {
            this.reporting = false;
        }
    }

    private readStored(): StoredAdClick | null {
        try {
            const raw = window.localStorage.getItem(AdClickService.STORAGE_KEY);

            if (!raw) return null;

            const parsed = JSON.parse(raw) as StoredAdClick;

            if (!parsed || !parsed.clickId || !AdClickService.CLICK_ID_PATTERN.test(parsed.clickId)) {
                this.clear();
                return null;
            }

            return {
                clickId: parsed.clickId,
                network: parsed.network || AdClickService.NETWORK,
                savedAt: Number(parsed.savedAt) || 0
            };
        } catch (e) {
            this.clear();
            return null;
        }
    }

    private clear(): void {
        try {
            window.localStorage.removeItem(AdClickService.STORAGE_KEY);
        } catch (e) {
            console.warn('Cant clear ad click id', e);
        }
    }
}

interface StoredAdClick {
    clickId: string;
    network: string;
    savedAt: number;
}
